/**
 * A centre: the organisation a therapist may belong to.
 *
 * Shapes mirror the API exactly, snake_case included. There is no mapping
 * layer here on purpose — a rename between two identical structures is a place
 * for a field to go missing, and every field on these types is one the backend
 * deliberately chose to expose.
 */

export type CenterType = "therapy" | "training";
export type CenterRole = "admin" | "therapist";
export type MembershipStatus = "invited" | "active" | "suspended";

export interface Center {
    id: string;
    name: string;
    type: CenterType;
    legal_name: string | null;
    tax_id: string | null;
    address: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    timezone: string;
    logo_url: string | null;
    /** Whether therapy type and template are offered when creating a session. */
    therapy_fields_enabled: boolean;
    /** Which kinds of session the centre runs. A mode switched off is offered nowhere. */
    individual_sessions_enabled: boolean;
    couple_sessions_enabled: boolean;
    group_sessions_enabled: boolean;
    created_at: string;
}

export type SessionMode = "individual" | "couple" | "group";

/** The caller's own centre and standing in it, from `GET /centers/me`. */
export interface CenterMembershipView {
    center: Center;
    roles: CenterRole[];
    status: MembershipStatus;
    is_admin: boolean;
    is_practising_therapist: boolean;
}

/**
 * One member of the centre, as an administrator sees them.
 *
 * Account and role only. No patient, session, agenda or clinical field appears
 * here, and none may be added: an administrator runs the practice, they do not
 * read its clinical record.
 */
export interface CenterMember {
    membership_id: string;
    therapist_id: string;
    first_name: string;
    last_name: string;
    email: string;
    roles: CenterRole[];
    status: MembershipStatus;
    is_practising_therapist: boolean;
    joined_at: string | null;
}

/**
 * A partial edit. A key left out is left alone; a key sent empty is cleared.
 * Only the keys present are sent, which is what keeps those two apart.
 */
export interface CenterUpdateInput {
    name?: string;
    legal_name?: string;
    tax_id?: string;
    address?: string;
    contact_email?: string;
    contact_phone?: string;
    timezone?: string;
    logo_url?: string;
    therapy_fields_enabled?: boolean;
    individual_sessions_enabled?: boolean;
    couple_sessions_enabled?: boolean;
    group_sessions_enabled?: boolean;
}

export interface CenterRegistrationInput {
    name: string;
    type: CenterType;
    legal_name?: string;
    tax_id?: string;
    address?: string;
    contact_email?: string;
    contact_phone?: string;
    timezone?: string;
    owner_first_name: string;
    owner_last_name: string;
    owner_email: string;
    owner_password: string;
    /** Whether the person signing up also treats people in the centre. */
    owner_practises: boolean;
    country?: string;
    language?: string;
    /** Centre invitation code. A centre is Plus-only, and for now invited. */
    invitation_token?: string;
}

export interface CenterRegistrationResponse {
    center: Center;
    therapist: { id: string; email: string };
    mfa: { qr_code_base64: string };
}

export type InvitationState = "pending" | "accepted" | "revoked" | "expired";

/** How the centre charges for the patients it holds. */
export interface CenterPaymentSettings {
    stripe: {
        connected: boolean;
        account_display_name: string | null;
        available: boolean;
    };
    default_payment_concept: string | null;
    default_payment_amount: string | null;
    payment_reminders_enabled: boolean;
}

export interface CenterPaymentSettingsInput {
    default_payment_concept?: string | null;
    default_payment_amount?: string | null;
    payment_reminders_enabled?: boolean;
}

/**
 * How full a centre is. Every member holds a seat (and with it Plus), and so
 * does every invitation still waiting to be used.
 */
export interface CenterSeats {
    seats: number;
    used: number;
    available: number;
}

/** An invitation as its centre's administrator sees it. */
export interface CenterInvitation {
    id: string;
    email: string;
    roles: CenterRole[];
    state: InvitationState;
    expires_at: string;
    created_at: string | null;
    /**
     * The join link. Present whether or not the email went out: delivery is
     * best-effort, and an administrator who cannot see the link cannot recover
     * from a bounce, a spam folder or a typo.
     */
    join_url: string;
}

export interface CenterInvitationInput {
    email: string;
    roles: CenterRole[];
    /** The invitation email's language. */
    language?: "es" | "en";
}

/**
 * What the holder of a join link is told before they act on it.
 *
 * The address is masked on purpose — a leaked token should give up the centre's
 * name and nothing more.
 */
export interface CenterInvitationPreview {
    center_name: string;
    center_type: CenterType;
    roles: CenterRole[];
    state: InvitationState;
    masked_email: string;
    is_usable: boolean;
}

/** A patient as the centre's administrator sees them: metadata, never content. */
export interface CenterPatient {
    patient_id: string;
    first_name: string;
    last_name: string;
    therapist_id: string;
    session_count: number;
    last_session_at: string | null;
    is_anonymous: boolean;
    /** The address the centre already holds. It identifies — it is how two
     *  records of the same person are told from two different people — and it
     *  is not an invitation to contact anybody. */
    contact_email?: string | null;
    /** A training centre's student: their course and the year of it. */
    course_id?: string | null;
    academic_year?: number | null;
}

/** One patient opened by the administrator: the row, plus where they sit. Still metadata. */
export interface CenterPatientDetail extends CenterPatient {
    in_center_since: string | null;
    groups: { id: string; name: string | null }[];
    profile?: StudentProfile | null;
}

/** What an administrator corrects on a student's record; only what is sent changes. */
export interface StudentUpdate {
    first_name?: string;
    last_name?: string;
    contact_email?: string;
    contact_phone?: string;
    /** E.164, or null to clear it. */
    whatsapp_phone?: string | null;
    course_id?: string;
    academic_year?: number | null;
    pronouns?: string | null;
    /** `YYYY-MM-DD`. */
    date_of_birth?: string | null;
    sex?: string | null;
    street?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    timezone?: string | null;
}

/** A training centre's student's record, as their school keeps it. Null for a therapy centre. */
export interface StudentProfile {
    contact_phone: string | null;
    /** Where WhatsApp messages go, in E.164 form. */
    whatsapp_phone?: string | null;
    /** The patient's own switch: false when they turned WhatsApp off. */
    whatsapp_opt_in?: boolean | null;
    pronouns: string | null;
    /** `YYYY-MM-DD`. */
    date_of_birth: string | null;
    sex: string | null;
    street: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
    timezone: string | null;
}

/** Where a training centre's course stands. */
export type CourseStatus = "planned" | "active" | "finished" | "cancelled";

export const COURSE_STATUSES: CourseStatus[] = ["planned", "active", "finished", "cancelled"];

/**
 * An academic year, kept as the year it starts: 2025 is 2025/26. Written out
 * the way a school says it.
 */
export function formatAcademicYear(year: number): string {
    return `${year}/${String((year + 1) % 100).padStart(2, "0")}`;
}

/** The academic year running on `now`: from August on, the one starting this year. */
export function currentAcademicYear(now: Date = new Date()): number {
    return now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
}

/** How many academic years past the current one can be chosen. */
export const ACADEMIC_YEARS_AHEAD = 10;

/**
 * The academic years to choose from: the current one and the ten after it, in
 * order — plus one already on a record, however old, so it still shows.
 */
export function academicYearOptions(
    now: Date = new Date(),
    keep?: number | null
): { value: string; label: string }[] {
    const current = currentAcademicYear(now);
    const years = new Set<number>();
    for (let year = current; year <= current + ACADEMIC_YEARS_AHEAD; year += 1) years.add(year);
    if (keep) years.add(keep);
    return [...years]
        .sort((a, b) => a - b)
        .map((year) => ({ value: String(year), label: formatAcademicYear(year) }));
}

/** One phase (or module) of a course's pedagogical plan. */
export interface CoursePhase {
    /** Kept through edits; absent on a module not saved yet. Sent back to keep it. */
    id?: string;
    name: string;
    description?: string | null;
    /** `YYYY-MM-DD`, or null when not decided yet. */
    starts_on?: string | null;
    ends_on?: string | null;
    topics: string[];
    summary?: string | null;
}

/** A course a training centre runs, with how many students it has. */
export interface CenterCourse {
    id: string;
    /** The public name: the one its students read. */
    name: string;
    /** How the centre's staff call it; what the administration lists it by. */
    internal_name: string;
    /** `YYYY-MM-DD`, or null when not decided yet. */
    starts_on: string | null;
    ends_on: string | null;
    status: CourseStatus;
    phases: CoursePhase[];
    students: number;
}

export interface CourseInput {
    name?: string;
    internal_name?: string;
    starts_on?: string | null;
    ends_on?: string | null;
    status?: CourseStatus;
    /** Sent whole: it replaces the course's plan. */
    phases?: CoursePhase[];
}

/** One course of a batch: created, or the code it was refused with. */
export interface CourseBatchResult {
    index: number;
    internal_name: string;
    course: CenterCourse | null;
    error: string | null;
}

/** One address of a batch of invitations. */
export interface InvitationBatchResult {
    email: string;
    invitation: CenterInvitation | null;
    error: string | null;
}

/** A student as the administrator of a training centre takes them on. */
export interface StudentInput {
    first_name: string;
    last_name: string;
    /** Required: how the student is invited to their portal. */
    contact_email: string;
    /** Required, as on a therapist's own patient form. */
    contact_phone: string;
    /** Optional, E.164: where WhatsApp messages go. */
    whatsapp_phone?: string | null;
    pronouns?: string | null;
    /** `YYYY-MM-DD`. */
    date_of_birth?: string | null;
    sex?: string | null;
    street?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    timezone?: string | null;
    /** The course, by id (a form picks it) or by internal name (a file names it). */
    course_id?: string;
    course_name?: string;
    mentor_email: string;
    academic_year?: number | null;
    /** Rows sharing a group name become one group of their mentor. */
    group?: string | null;
    /** The two rows sharing a couple name become a couple of their therapist. */
    couple?: string | null;
    /**
     * The centre's own fields, by field id. Sent, the required ones are asked
     * for; left out, none is (as on the patient form).
     */
    custom_values?: Record<string, string | null>;
    /** The patient's intake profile, by field key. A therapy centre's patients only. */
    intake_values?: Record<string, string | null>;
}

/** One student of a batch, by position: created, or why not. */
export interface StudentBatchResult {
    index: number;
    patient_id: string | null;
    group_id: string | null;
    couple_id?: string | null;
    error: string | null;
    /** The custom field a refusal is about. */
    field_id?: string | null;
    /** The intake field a refusal is about. */
    intake_field?: string | null;
}

/**
 * A couple as the centre sees it. `members` names only the partners whose
 * record the centre holds; `fully_in_center` false means one partner never
 * consented, and the couple cannot be handed over until they do.
 */
export interface CenterCouple {
    couple_id: string;
    display_name: string | null;
    therapist_id: string;
    members: CenterPatient[];
    fully_in_center: boolean;
    /** The couple's own sessions, held together. Absent where not asked for. */
    session_count?: number;
    last_session_at?: string | null;
}

/** A group as the centre sees it — the same rules as a couple. */
export interface CenterGroup {
    group_id: string;
    display_name: string | null;
    therapist_id: string;
    members: CenterPatient[];
    fully_in_center: boolean;
    /** The group's own sessions. Absent where not asked for. */
    session_count?: number;
    last_session_at?: string | null;
}

/** The caseload's couples and groups, listed beside its patients. */
export interface CenterUnits {
    couples: CenterCouple[];
    groups: CenterGroup[];
}

/** What a member treats in the centre, to be handed over before they leave. */
export interface MemberCaseload {
    individuals: CenterPatient[];
    couples: CenterCouple[];
    groups: CenterGroup[];
}

export interface MemberHandoverInput {
    patients: { patient_id: string; to_therapist_id: string }[];
    couples: { couple_id: string; to_therapist_id: string }[];
    groups: { group_id: string; to_therapist_id: string }[];
    reason?: string;
}

export interface CenterPatientPage {
    items: CenterPatient[];
    total: number;
}

export interface PatientReassignment {
    id: string;
    patient_id: string;
    from_therapist_id: string;
    to_therapist_id: string;
    actor_id: string;
    reason: string | null;
    created_at: string;
}

/** What is handed over: one person, a couple or a group. */
export type HandoverKind = "individual" | "couple" | "group";

export interface ReassignmentPreviewInput {
    kind: HandoverKind;
    id: string;
    toTherapistId: string;
}

/** When a booking is — a day (`YYYY-MM-DD`), an hour, minutes. Nothing else. */
export interface BookingSlot {
    date: string;
    hour: string;
    duration: number;
}

/**
 * A booking that would land on top of one the new therapist already has.
 * `same_slot`: the very same time — the handover is refused
 * (`handover_slot_taken`) until one of the two is rescheduled.
 */
export interface BookingClash extends BookingSlot {
    appointment_id: string;
    clashes_with: BookingSlot;
    same_slot: boolean;
}

/** A booking on Zoom, Google Meet or Teams: its meeting stays in the previous therapist's account. */
export interface LinkLeftBehind {
    appointment_id: string;
    date: string;
    hour: string;
    platform: "zoom" | "google_meet" | "microsoft_teams" | string;
}

/**
 * What a handover would do to the bookings still to come, before it is
 * confirmed. Warnings, never a refusal. Times only: no title, no link, no names.
 */
export interface ReassignmentPreview {
    future_appointments: number;
    clashes: BookingClash[];
    external_links: LinkLeftBehind[];
}

/**
 * A booking, with no meeting link and no title — or a session held without
 * one, which has no booking id and no booking status.
 */
export interface CenterAppointment {
    appointment_id: string | null;
    therapist_id: string;
    patient_id: string | null;
    patient_name: string | null;
    date: string;
    hour: string;
    duration: number;
    status: string | null;
    ubication: string;
    type: string;
    /**
     * A group's booking: how many are in it. With no `patient_name` the
     * group has no name, and the agenda calls it by this — never by who is in it.
     */
    group_member_count?: number | null;
    /** Whether its session took place: null for a booking with no session. */
    session_status?: "pending" | "finished" | "cancelled" | null;
    /** Whether its session was paid: null for a booking with no session. */
    payment_status?: "pending" | "paid" | null;
    /** The session it is held as, if any. */
    session_id?: string | null;
    /** Booked through Alchely's agenda, or written up without a booking. */
    scheduled_in_alchely?: boolean;
}

/** The agenda narrowed by its sessions' statuses and by how it was booked. */
/** One page of the agenda, and how many rows it has in all. */
export interface CenterAppointmentPage {
    items: CenterAppointment[];
    total: number;
}

export interface AgendaStatusFilters {
    sessionStatus?: string;
    paymentStatus?: string;
    /** "true": booked in Alchely; "false": written up without a booking. */
    scheduledInAlchely?: string;
}

export interface TherapistSessionReport {
    therapist_id: string;
    sessions: number;
    patients: number;
    minutes: number;
    completed: number;
    pending: number;
}

export interface TherapistAgendaReport {
    therapist_id: string;
    scheduled: number;
    completed: number;
    cancelled: number;
    booked_by_patient: number;
}

/** How the reports over time group their periods. */
export type { ReportGranularity } from "../../../shared/components/reports/types";

/** One period of work across the centre. `period` is its first day. */
export interface ActivityPeriod {
    period: string;
    sessions: number;
    patients_seen: number;
    /** People whose record became the centre's in this period. */
    new_patients: number;
}

/** Money is sent as a decimal string, as it is everywhere else. */
export interface PaymentPeriod {
    period: string;
    paid: string;
    pending: string;
}

export interface PaymentStatusTotal {
    status: string;
    count: number;
    amount: string;
}

export interface CenterPaymentReport {
    periods: PaymentPeriod[];
    statuses: PaymentStatusTotal[];
}

export interface CenterPayment {
    payment_id: string;
    therapist_id: string;
    patient_id: string;
    patient_name: string;
    total: string;
    status: string;
    due_date: string | null;
    created_at: string;
}

export interface CenterPaymentPage {
    items: CenterPayment[];
    total: number;
}

export type ToolPolicyState = "enabled" | "disabled";

export interface CenterToolPolicy {
    tool_id: string;
    slug: string;
    name: string;
    state: ToolPolicyState;
    /** How many of the centre's therapists use it — what a decision costs. */
    adopted_by: number;
    /**
     * The end of the period this module has already been charged for.
     *
     * Not about use — `state` answers that, and a module the centre switches
     * off goes off at once — but about money: money is never given back, so
     * until this date the centre may switch it on again, as often as it
     * likes, without paying for the same days twice.
     *
     * Null for everything nobody has paid for, which is nearly every row.
     */
    paid_until?: string | null;
    /**
     * What switching this on adds to the centre's bill, per seat, per month.
     * Zero for everything that comes with the plan — which is nearly all of
     * it. Sent so the screen can say what a decision costs before it is
     * taken, rather than leaving it to the next invoice.
     */
    monthly_price_cents?: number;
    /**
     * And per seat per year, the annual discount already in it. A centre
     * billed yearly is charged the yearly price, so that is the figure its
     * screen has to quote.
     */
    annual_price_cents?: number;
}

export interface CenterConsentResult {
    patient_id: string;
    center_id: string;
    consent_version: string;
    consented_at: string;
    /** Everybody else brought in with them, when their group came in whole. */
    brought_with_group?: string[];
}

/**
 * Bringing a patient into the centre. A patient in a group with members still
 * outside it is refused alone (`consent_splits_group`); `withGroup` brings
 * everybody who has to come with them.
 */
export interface RecordCenterConsentInput {
    patientId: string;
    withGroup?: boolean;
}
